import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  type TextFieldProps,
} from "@mui/material";
import { LicenseRadioOption } from "Components/Shared/Inputs/Form/PartyLicenseSelector/LicenseRadioOption";
import {
  LicensePaymentStatus,
  type PartyLicenseDto,
} from "Infrastructure/Api/Api";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

type Props<
  FormType extends FieldValues,
  TransformedFieldValues = FormType,
> = TextFieldProps & {
  name: FieldPath<FormType>;
  label?: string;
  control: Control<FormType, unknown, TransformedFieldValues>;
  disabled?: boolean;
  licenses: Array<PartyLicenseDto>;
};

export const PartyLicenseSelector = <T extends object, V extends object>({
  control,
  name,
  label,
  licenses,
}: Props<T, V>) => (
  <Controller
    control={control}
    name={name}
    render={({
      field: { onChange, value, ...field },
      fieldState: { error },
    }) => (
      <FormControl
        component="fieldset"
        sx={{ width: "100%", marginBottom: 2 }}
        error={!!error}
      >
        <FormLabel
          component="legend"
          sx={{
            marginBottom: 1,
            fontWeight: 400,
            color: (theme) => theme.palette.text.primary,
          }}
        >
          {label}
        </FormLabel>
        <RadioGroup
          {...field}
          value={value}
          onChange={({ target: { value } }) => onChange(value)}
        >
          {licenses.map(
            ({ publicID, code, licenseNo, paymentStatus }, index) => {
              const isSelected = value === publicID;
              const isDisabled = paymentStatus !== LicensePaymentStatus.Success;

              return (
                <FormControlLabel
                  key={publicID}
                  value={publicID}
                  control={
                    <Radio
                      sx={{ display: "none" }}
                      checked={isSelected}
                      disabled={isDisabled}
                    />
                  }
                  sx={{
                    width: "100%",
                    marginBottom: index < licenses.length - 1 ? 2 : 0,
                    marginLeft: 0,
                    "& .MuiFormControlLabel-label": {
                      width: "100%",
                      margin: 0,
                    },
                  }}
                  label={
                    <LicenseRadioOption
                      code={code}
                      licenseNo={licenseNo || ""}
                      isSelected={isSelected}
                      isDisabled={isDisabled}
                    />
                  }
                />
              );
            },
          )}
        </RadioGroup>
        <FormHelperText sx={{ margin: 0 }}>{error?.message}</FormHelperText>
      </FormControl>
    )}
  />
);
